import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, TextField, Button, List, ListItem, ListItemText, IconButton, Stack, Divider } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const Collections: React.FC = () => {
  const { authenticated, keycloak } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [slug, setSlug] = useState('my-issues');
  const [name, setName] = useState('My Issues');
  const [description, setDescription] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      setLoading(true);
      setError(null);
      try {
        const resp = await apiService.listCollections();
        if (cancelled) return;
        setItems(Array.isArray(resp.data?.items) ? resp.data.items : (Array.isArray(resp.data?.collections) ? resp.data.collections : []));
      } catch (e: any) {
        setError('Failed to load collections');
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authenticated, keycloak?.token]);

  const handleCreate = async () => {
    try {
      setError(null);
      const payload = { slug: slug.trim(), name: name.trim(), description: description || null } as any;
      const resp = await apiService.createCollection(payload);
      const created = resp?.data?.collection || resp?.data;
      if (!created?.id) throw new Error('Missing created record');
      setItems(prev => [{ ...payload, ...created }, ...prev]);
      setError(null);
      setSlug('');
      setName('');
      setDescription('');
    } catch (e: any) {
      setError('Failed to create collection');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiService.deleteCollection(id);
      setItems(items.filter((x) => x.id !== id));
    } catch {}
  };

  const loadItems = async (id: number) => {
    try {
      const resp = await apiService.listCollectionItems(id);
      const rec = items.find((x) => x.id === id);
      if (rec) {
        rec.items = Array.isArray(resp.data?.items) ? resp.data.items : [];
        setItems([...items]);
      }
    } catch {}
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Collections</Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Create a collection</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField label="Slug" value={slug} onChange={(e) => setSlug(e.target.value)} size="small" sx={{ minWidth: 180 }} />
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} size="small" sx={{ minWidth: 220 }} />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} size="small" fullWidth />
          <Button variant="contained" onClick={handleCreate}>Create</Button>
        </Stack>
      </Paper>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>My collections</Typography>
        {loading ? (
          <Typography>Loading…</Typography>
        ) : (
          <List>
            {items.map((c: any) => (
              <Box key={c.id} sx={{ mb: 2 }}>
                <ListItem
                  secondaryAction={
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(c.id)}>
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={c.name}
                    secondary={c.visibility === 'public' && c.user_handle ? `Shareable: /c/${c.user_handle}/${c.slug}` : c.slug}
                    onClick={() => loadItems(c.id)}
                    sx={{ cursor: 'pointer' }}
                  />
                </ListItem>
                {Array.isArray(c.items) && c.items.length > 0 && (
                  <Box sx={{ pl: 4 }}>
                    {c.items.map((it: any) => (
                      <Typography key={it.id} variant="body2">{it.target_type}: {it.target_id}</Typography>
                    ))}
                    <Divider sx={{ mt: 1 }} />
                  </Box>
                )}
              </Box>
            ))}
            {items.length === 0 && <Typography variant="body2">No collections yet.</Typography>}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default Collections;
